import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { defer, Observable, Subject, throwError, timer } from 'rxjs';
import { catchError, concatMap, finalize, retry, shareReplay } from 'rxjs/operators';

const API_PATH = '/multilocationanalytics.v1.AnalyticsService/QueryMetrics';
export const THROTTLE_LIMIT = 20;
const MAX_RETRIES = 3;
const RETRY_BASE_DELAY_MS = 250;

export function isRetryableQueryMetricsError(error: unknown): boolean {
  return error instanceof HttpErrorResponse && (error.status === 0 || error.status >= 500);
}

@Injectable()
export class QueryMetricsThrottleInterceptor implements HttpInterceptor {
  private activeRequests = 0;
  private readonly delayedRequestTriggers: Subject<void>[] = [];
  private readonly requests: { [key: string]: Observable<HttpEvent<unknown>> } = {};

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!req.url.endsWith(API_PATH)) {
      return next.handle(req);
    }

    const requestBody = JSON.stringify(req.body);
    if (this.requests[requestBody]) {
      return this.requests[requestBody];
    }

    const queuedRequest: Observable<HttpEvent<unknown>> = defer(() => {
      this.activeRequests++;
      return next.handle(req).pipe(
        retry({
          count: MAX_RETRIES,
          delay: (error, retryCount) =>
            isRetryableQueryMetricsError(error)
              ? timer(RETRY_BASE_DELAY_MS * 2 ** (retryCount - 1))
              : throwError(() => error),
        }),
      );
    }).pipe(
      catchError((error) => {
        if (this.requests[requestBody] === queuedRequest) {
          delete this.requests[requestBody];
        }
        return throwError(() => error);
      }),
      finalize(() => this.onRequestFinalize()),
      shareReplay({ refCount: false, bufferSize: 1 }),
    );
    this.requests[requestBody] = queuedRequest;

    if (this.activeRequests < THROTTLE_LIMIT) {
      return queuedRequest;
    }

    const trigger = new Subject<void>();
    this.delayedRequestTriggers.push(trigger);
    return trigger.pipe(concatMap(() => this.requests[requestBody] ?? (this.requests[requestBody] = queuedRequest)));
  }

  private onRequestFinalize(): void {
    this.activeRequests--;
    if (this.delayedRequestTriggers.length) {
      const firstTrigger = this.delayedRequestTriggers.shift();
      firstTrigger.next();
      firstTrigger.complete();
    }
  }
}
